import { Link, router, useForm, usePage } from "@inertiajs/react"
import { Building2, PlusIcon } from "lucide-react"
import { type ReactNode, useState, useEffect } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  AvailableUser,
  OnboardingStatus,
  PaginationMeta,
  PropertyRow,
  PropertyStatus,
  PropertyType,
  RegionOption,
} from "@/lib/types"
import type { SharedProps } from "@/types"

interface Filters {
  search?: string
  region_id?: string
  property_type?: string
  onboarding_status?: string
  status?: string
}

interface Props {
  properties: PropertyRow[]
  meta: PaginationMeta
  filters: Filters
  all_regions: RegionOption[]
  available_users: AvailableUser[]
  urls: { index: string; create: string }
}

function statusVariant(status: PropertyStatus): "default" | "outline" | "destructive" {
  if (status === "active") return "default"
  if (status === "suspended") return "destructive"
  return "outline"
}

function onboardingClassName(status: OnboardingStatus): string {
  const map: Record<OnboardingStatus, string> = {
    pending:         "border-yellow-300 bg-yellow-50 text-yellow-700",
    materials_sent:  "border-blue-300 bg-blue-50 text-blue-700",
    wifi_configured: "border-purple-300 bg-purple-50 text-purple-700",
    live:            "border-green-300 bg-green-50 text-green-700",
  }
  return map[status] ?? ""
}

const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  hotel:     "Hotel",
  bnb:       "B&B",
  apartment: "Apartment",
  hostel:    "Hostel",
  villa:     "Villa",
  other:     "Other",
}

function onboardingLabel(status: OnboardingStatus): string {
  const map: Record<OnboardingStatus, string> = {
    pending:         "Pending",
    materials_sent:  "Materials sent",
    wifi_configured: "WiFi configured",
    live:            "Live",
  }
  return map[status] ?? status
}

function FilterBar({
  filters,
  allRegions,
  indexUrl,
}: {
  filters: Filters
  allRegions: RegionOption[]
  indexUrl: string
}) {
  const [search, setSearch] = useState(filters.search ?? "")

  function update(key: keyof Filters, value: string | undefined) {
    const next = { ...filters, [key]: value || undefined }
    router.get(indexUrl, next, { preserveState: true, replace: true })
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Input
        className="h-8 w-52 text-sm"
        placeholder="Search by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") update("search", search || undefined) }}
        onBlur={() => update("search", search || undefined)}
      />

      <Select
        value={filters.region_id ?? "any"}
        onValueChange={(v) => update("region_id", v === "any" ? undefined : v)}
      >
        <SelectTrigger className="h-8 w-44 text-sm">
          <SelectValue placeholder="All regions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All regions</SelectItem>
          {allRegions.map((r) => (
            <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.property_type ?? "any"}
        onValueChange={(v) => update("property_type", v === "any" ? undefined : v)}
      >
        <SelectTrigger className="h-8 w-36 text-sm">
          <SelectValue placeholder="All types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All types</SelectItem>
          {(["hotel", "bnb", "apartment", "hostel", "villa", "other"] as PropertyType[]).map((t) => (
            <SelectItem key={t} value={t}>{PROPERTY_TYPE_LABELS[t]}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? "any"}
        onValueChange={(v) => update("status", v === "any" ? undefined : v)}
      >
        <SelectTrigger className="h-8 w-36 text-sm">
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All statuses</SelectItem>
          <SelectItem value="draft">Draft</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="suspended">Suspended</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.onboarding_status ?? "any"}
        onValueChange={(v) => update("onboarding_status", v === "any" ? undefined : v)}
      >
        <SelectTrigger className="h-8 w-48 text-sm">
          <SelectValue placeholder="All onboarding" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All onboarding</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="materials_sent">Materials sent</SelectItem>
          <SelectItem value="wifi_configured">WiFi configured</SelectItem>
          <SelectItem value="live">Live</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

interface NewPropertyFormProps {
  allRegions: RegionOption[]
  availableUsers: AvailableUser[]
  createUrl: string
  onClose: () => void
}

function NewPropertyForm({ allRegions, availableUsers, createUrl, onClose }: NewPropertyFormProps) {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    slug: "",
    property_type: "hotel",
    region_id: "",
    owner_id: "",
  })
  const serverErrors = errors as Record<string, string | undefined>

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(createUrl, { onSuccess: onClose })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
      <div className="grid gap-1.5">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={data.name}
          onChange={(e) => setData("name", e.target.value)}
          required
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          value={data.slug}
          onChange={(e) => setData("slug", e.target.value)}
          required
        />
        {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="property_type">Type</Label>
        <Select value={data.property_type} onValueChange={(v) => setData("property_type", v)}>
          <SelectTrigger id="property_type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(["hotel", "bnb", "apartment", "hostel", "villa", "other"] as PropertyType[]).map((t) => (
              <SelectItem key={t} value={t}>{PROPERTY_TYPE_LABELS[t]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.property_type && <p className="text-xs text-destructive">{errors.property_type}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="region_id">Region</Label>
        <Select
          value={data.region_id || "none"}
          onValueChange={(v) => setData("region_id", v === "none" ? "" : v)}
        >
          <SelectTrigger id="region_id"><SelectValue placeholder="Select a region…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Select a region…</SelectItem>
            {allRegions.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {serverErrors.region_id && <p className="text-xs text-destructive">{serverErrors.region_id}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="owner_id">Owner</Label>
        <Select
          value={data.owner_id || "none"}
          onValueChange={(v) => setData("owner_id", v === "none" ? "" : v)}
        >
          <SelectTrigger id="owner_id"><SelectValue placeholder="Select an owner…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Select an owner…</SelectItem>
            {availableUsers.map((u) => (
              <SelectItem key={u.id} value={String(u.id)}>
                {u.name ? `${u.name} (${u.email})` : u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {serverErrors.owner_id && <p className="text-xs text-destructive">{serverErrors.owner_id}</p>}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={processing}>
          {processing ? "Creating…" : "Create property"}
        </Button>
      </div>
    </form>
  )
}

function PaginationControls({ meta, indexUrl }: { meta: PaginationMeta; indexUrl: string }) {
  if (meta.pages <= 1) return null

  return (
    <div className="flex items-center justify-between py-3 text-sm text-muted-foreground">
      <span>{meta.count} propert{meta.count !== 1 ? "ies" : "y"}</span>
      <div className="flex gap-2">
        {meta.page > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => router.get(indexUrl, { page: meta.page - 1 }, { preserveState: true })}
          >
            Previous
          </Button>
        )}
        <span className="flex items-center px-2">Page {meta.page} of {meta.pages}</span>
        {meta.page < meta.pages && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => router.get(indexUrl, { page: meta.page + 1 }, { preserveState: true })}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  )
}

export default function PropertiesIndex({ properties, meta, filters, all_regions, available_users, urls }: Props) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [navigating, setNavigating] = useState(false)
  useEffect(() => {
    const off1 = router.on("start", () => setNavigating(true))
    const off2 = router.on("finish", () => setNavigating(false))
    return () => { off1(); off2() }
  }, [])

  const hasFilters = !!(filters.search || filters.region_id || filters.property_type || filters.status || filters.onboarding_status)

  const { props } = usePage<SharedProps>()
  const isAdmin = props.user_context?.is_admin

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Properties</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">All properties across the platform.</p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setSheetOpen(true)}>
            <PlusIcon className="mr-1.5 size-4" />
            New property
          </Button>
        )}
      </div>

      <div className="mb-4">
        <FilterBar filters={filters} allRegions={all_regions} indexUrl={urls.index} />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Onboarding</TableHead>
              <TableHead>Last session</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {navigating ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : properties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  {hasFilters ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">
                      No properties match your filters.
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Building2 className="mb-4 h-10 w-10 text-muted-foreground opacity-30" aria-hidden="true" />
                      <p className="text-sm font-medium text-muted-foreground">No properties found.</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Onboard your first property to get started.
                      </p>
                      {isAdmin && (
                        <Button size="sm" className="mt-4" onClick={() => setSheetOpen(true)}>
                          Onboard Property
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              properties.map((property) => (
                <TableRow key={property.id}>
                  <TableCell className="font-medium">
                    <Link href={property.url} className="hover:underline">
                      {property.name}
                    </Link>
                    <span className="ml-1.5 text-xs text-muted-foreground">/{property.slug}</span>
                  </TableCell>
                  <TableCell className="capitalize text-muted-foreground">{property.property_type}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {property.region_name ?? <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {property.owner_name || property.owner_email}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(property.status)}>{property.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={onboardingClassName(property.onboarding_status)}>
                      {onboardingLabel(property.onboarding_status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {property.last_guest_session_at
                      ? new Date(property.last_guest_session_at).toLocaleDateString()
                      : <span className="italic">Never</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(property.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationControls meta={meta} indexUrl={urls.index} />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>New property</SheetTitle>
          </SheetHeader>
          <NewPropertyForm
            allRegions={all_regions}
            availableUsers={available_users}
            createUrl={urls.create}
            onClose={() => setSheetOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </div>
  )
}

PropertiesIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
